/**
 * Shared "is this room real to the API?" logic for the Basics step.
 *
 * The API's own invariant (confirmed via live 400s: "N bedrooms declared but
 * only M configured") is that the declared `bedrooms` count must equal the
 * number of rooms that actually have at least one bed — not just the number
 * of room cards in local state. A room card added via the bedrooms "+"
 * counter starts as a local-only `temp-` row with `beds: {}`; it only
 * becomes a real server row once BedSetupModal's "Done" is clicked (that
 * button is itself disabled while totalBeds is 0). If the user abandons that
 * flow — closing the modal via a step-rail navigation instead of Cancel, a
 * refresh, or a closed tab — the zero-bed temp row survives in the persisted
 * draft while `bedrooms` already counts it, permanently desyncing the two
 * counts until every future room delete fails with the 400 above.
 */

export function totalBeds(beds: Record<string, number> | undefined): number {
  return Object.values(beds ?? {}).reduce((sum, count) => sum + count, 0);
}

/** A room only exists on the server (or is about to) once it has at least one bed. */
export function isConfiguredRoom(room: { beds: Record<string, number> }): boolean {
  return totalBeds(room.beds) > 0;
}

/** Drops zero-bed `temp-` rows — they were never sent to createPropertyRoom, so they have no server-side row to reconcile against. Real (non-temp) rooms are never pruned, even if their beds were somehow cleared to 0 locally, since deleting them would silently discard already-saved server data. */
export function pruneUnconfiguredTempRooms<T extends { id: string; beds: Record<string, number> }>(rooms: T[]): T[] {
  return rooms.filter((r) => !r.id.startsWith("temp-") || isConfiguredRoom(r));
}

export function getBedsSummary(beds: Record<string, number>): { summary: string; count: number } {
  const count = totalBeds(beds);
  return { summary: `${count} bed(s)`, count };
}

/** Deleting a room can also surface other abandoned zero-bed temp rows for pruning — see pruneUnconfiguredTempRooms. */
export function deleteRoom<T extends { id: string; beds: Record<string, number> }>(rooms: T[], roomId: string): T[] {
  return pruneUnconfiguredTempRooms(rooms.filter((r) => r.id !== roomId));
}
